import React, { createContext, useContext, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from '../../axios.js';

const UploadContext = createContext();

export const UploadProvider = ({ children }) => {
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [viewingDocument, setViewingDocument] = useState(null);
  const baseUrl = process.env.REACT_APP_TRANSACTIONS;
  const location = useLocation();
  const project_code = location.state?.TxCode;

  const autoUploadFile = async (file, onProgress) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        `${baseUrl}tasks/document/upload/${project_code}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total && onProgress) {
              const percent = Math.round((e.loaded * 100) / e.total);
              onProgress(percent);
            }
          },
        }
      );

      if (res.data.success) {
        const filename = res.data.filename;
        setFiles((prev) => [...prev, filename]);
        return filename;
      }

      throw new Error(res.data.message || "Upload failed");
    } catch (err) {
      console.error("Upload error:", err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const autoRemoveFile = async (filename) => {
    try {
      const res = await axios.delete(
        `${baseUrl}tasks/document/remove/${project_code}/${filename}`
      );

      if (res.data.success) {
        setFiles((prev) => prev.filter((f) => f !== filename));
      }

      return res.data;
    } catch (err) {
      console.error("Delete error:", err);
      throw err;
    }
  };

  const autoUploadEditor = async (file, onProgress) => {
    try {
      setUploading(true);

      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        `${baseUrl}tasks/document/upload/editor/${project_code}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total && onProgress) {
              const percent = Math.round((e.loaded * 100) / e.total);
              onProgress(percent);
            }
          },
        }
      );

      if (res.data.success) {
        return res.data;
      }

      throw new Error(res.data.message || "Upload failed");
    } catch (err) {
      console.error("Upload error:", err);
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const autoRemoveEditor = async (fileurl) => {
    try {
      const res = await axios.delete(fileurl);
      return res.data;
    } catch (err) {
      console.error("Delete error:", err);
      throw err;
    }
  };

  const getDocument = async (filename, code = undefined) => {
    try {
      const pcode = project_code || code;
      const res = await axios.get(
        `${baseUrl}tasks/document/view/${pcode}/${filename}`,
        { responseType: "blob" }
      );

      if (!res.data) throw new Error("Failed to load document");

      const ext = filename.split('.').pop().toLowerCase();
      let type = "unknown";

      if (["xlsx", "xls"].includes(ext)) type = "excel";
      else if (ext === "pdf") type = "pdf";
      else if (["png", "jpg", "jpeg", "gif", "bmp", "webp"].includes(ext)) type = "image";

      const blob = new Blob([res.data], { type: res.headers["content-type"] });

      setViewingDocument({ filename, type, data: blob });
      return { filename, type, data: blob };
    } catch (err) {
      console.error("View document error:", err);
      throw err;
    }
  };

  const clearViewingDocument = () => setViewingDocument(null);

  return (
    <UploadContext.Provider
      value={{
        uploading,
        files,
        viewingDocument,
        autoUploadFile,
        autoUploadEditor,
        autoRemoveFile,
        autoRemoveEditor,
        getDocument,
        clearViewingDocument
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};

export const useUpload = () => {
  const context = useContext(UploadContext);
  if (!context) {
    throw new Error("useUpload must be used within an UploadProvider");
  }
  return context;
};

export default UploadContext;
